import { useButton } from '@react-aria/button';
import PropTypes from 'prop-types';
import React__default from 'react';
import { useFloatingLabelFormElement } from '../../contexts/internal/floating-label/index.js';
import { useSwanListboxButtonContext } from './listbox-button.context.js';
import { useSelectedItems } from './use-selected-items.hook.js';
import { useSwanPopover } from '../popover/popover.context.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { RenderComp } from '../core/render-comp.component.js';
import { assignRefs } from '../../../core/utilities/ref.utils.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const propTypes = {
    /**
   * Content to be displayed before the selected items.
   */ labelPrefix: PropTypes.node,
    /**
   * Placeholder Text to display when no item is selected, instead of the first item.
   */ placeholder: PropTypes.node
};
const propKeysToRemove = Object.keys(propTypes);
const SelectedOptions = ({ className, placeholder })=>{
    const { selectedItems, selectedItemsOrFirst } = useSelectedItems();
    let selectedText = '';
    if (selectedItems.length === 0 && placeholder != null) {
        selectedText = placeholder;
    } else if (selectedItemsOrFirst.length === 1) {
        var _item_props;
        const item = selectedItemsOrFirst[0];
        var _item_props_label, _ref, _ref1;
        selectedText = (_ref1 = (_ref = (_item_props_label = item === null || item === void 0 ? void 0 : (_item_props = item.props) === null || _item_props === void 0 ? void 0 : _item_props.label) !== null && _item_props_label !== void 0 ? _item_props_label : item === null || item === void 0 ? void 0 : item.rendered) !== null && _ref !== void 0 ? _ref : item === null || item === void 0 ? void 0 : item.textValue) !== null && _ref1 !== void 0 ? _ref1 : '';
    } else {
        // concatenate and flatten the JSX for every selected item
        selectedText = selectedItemsOrFirst.reduce((arr, item, i)=>{
            var _item_value;
            // if the item has a "label" prop, use that, otherwise fallback to the rendered jsx
            const props = item === null || item === void 0 ? void 0 : item.props;
            var _item_value_toString;
            const key = (_item_value_toString = item === null || item === void 0 ? void 0 : (_item_value = item.value) === null || _item_value === void 0 ? void 0 : _item_value.toString()) !== null && _item_value_toString !== void 0 ? _item_value_toString : i;
            var _props_label, _ref;
            const itemText = /*#__PURE__*/ React__default.createElement(React__default.Fragment, {
                key: key
            }, (_ref = (_props_label = props.label) !== null && _props_label !== void 0 ? _props_label : item === null || item === void 0 ? void 0 : item.rendered) !== null && _ref !== void 0 ? _ref : item === null || item === void 0 ? void 0 : item.textValue);
            const items = [
                ...arr,
                itemText
            ];
            if (i < selectedItemsOrFirst.length - 1) {
                items.push(/*#__PURE__*/ React__default.createElement(React__default.Fragment, {
                    key: `${key},`
                }, ', '));
            }
            return items;
        }, []);
    }
    return /*#__PURE__*/ React__default.createElement("span", {
        className: className
    }, selectedText);
};
/**
 * @subcomponent Listbox
 */ const ListboxButton = renderWithRef('ListboxButton', {}, (_param, ref)=>{
    var { className, labelPrefix } = _param, props = _object_without_properties(_param, [
        "className",
        "labelPrefix"
    ]);
    const { triggerRef } = useSwanPopover();
    const { triggerProps, triggerDisabled } = useSwanListboxButtonContext();
    const combinedRef = assignRefs(ref, triggerRef);
    const { buttonProps } = useButton(triggerProps, triggerRef);
    const id = buttonProps.id;
    // make the button's id available to the context, so that a floating label can use it
    useFloatingLabelFormElement(_object_spread_props(_object_spread({}, props), {
        placeholder: undefined,
        id
    }));
    const classNames = new Set([
        'swan-listbox-button'
    ]);
    if (labelPrefix) classNames.add('swan-listbox-button-with-label');
    if (className) classNames.add(className);
    return /*#__PURE__*/ React__default.createElement(RenderComp, {
        root: "button",
        forwardedRef: combinedRef,
        classNames: classNames,
        props: _object_spread_props(_object_spread({}, props, buttonProps), {
            disabled: triggerDisabled
        }),
        propKeysToRemove: propKeysToRemove
    }, labelPrefix ? /*#__PURE__*/ React__default.createElement("span", {
        className: "swan-listbox-button-label"
    }, labelPrefix) : null, /*#__PURE__*/ React__default.createElement(SelectedOptions, {
        placeholder: props.placeholder
    }));
});

export { ListboxButton };
